<script setup lang="ts">
  import { ChevronUpIcon } from '@heroicons/vue/solid';
  import AnnotationIcon from '~/assets/Icons/AnnotationIcon.vue';
  import PieGraphFilledIcon from '~/assets/Icons/PieGraphFilledIcon.vue';
  import ComponentSection from '~/components/UI/Drawers/ComponentSection.vue';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import type { BudgetsDrawerType } from '~/types';
  import type { LineItem } from '~/types/budgets/budgetsDrawer';
  import TopToRightAngleIcon from '~/assets/Icons/TopToRightAngleIcon.vue';
  import BudgetExpensesStatus from '../../Others/BudgetExpensesStatus.vue';

  const props = defineProps<{
    items: BudgetsDrawerType['selected_budget']['line_items'];
    budgetId: string;
    budgetFinancialYear: string;
    budgetName: string;
  }>();

  // ── Read-only note modal ───────────────────────────────────────────────────
  const isModalVisible = ref(false);
  const modalNote = ref<string>('');

  const handleModalCancel = () => {
    isModalVisible.value = false;
    modalNote.value = '';
  };

  // ── Read-only breakdown modal ──────────────────────────────────────────────
  const viewBreakdownVisible = ref(false);
  const viewBreakdownIsCustom = ref(false);
  const viewBreakdownTotal = ref<number>(0);
  const viewBreakdownEntries = ref<{ month: string; amount: number }[]>([]);

  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const formatBreakdown = (calendar: LineItem['li_calendar']) => {
    if (!calendar?.li_breakdown) return [];
    return Object.entries(calendar.li_breakdown)
      .filter(([, v]) => Number(v) !== 0)
      .map(([key, val]) => ({
        month: MONTH_NAMES[Number(key) - 1] ?? key.charAt(0).toUpperCase() + key.slice(1),
        amount: Number(val)
      }));
  };

  const openBreakdownView = (calendar: LineItem['li_calendar'], amount?: number | string) => {
    viewBreakdownIsCustom.value = calendar?.li_isCustom === 'true';
    viewBreakdownEntries.value = formatBreakdown(calendar);
    viewBreakdownTotal.value =
      amount !== undefined ? Number(amount) : viewBreakdownEntries.value.reduce((s, e) => s + e.amount, 0);
    viewBreakdownVisible.value = true;
  };

  type StructuredItems = {
    main_line_item: LineItem;
    sub_line_items: LineItem[];
  };

  const structuredItems = computed(() => {
    return props.items.reduce(
      (acc, item) => {
        if (!acc[item.li_text]) {
          acc[item.li_text] = {
            main_line_item: {
              li_text: item.li_text,
              li_amount: 0,
              li_note: '',
              li_calendar: undefined,
              approved_amount: 0,
              unapproved_amount: 0,
              stats: {
                approved_expenses: { exceeding_budget: 0, within_budget: 0 },
                unapproved_expenses: { exceeding_budget: 0, within_budget: 0 },
                pending_expenses: {
                  pending_amount: 0,
                  pending_within_budget: 0,
                  pending_exceeding_budget: 0
                },
                draft_expenses: {
                  draft_amount: 0,
                  draft_within_budget: 0,
                  draft_exceeding_budget: 0
                },
                total_budget_amount: 0
              }
            },
            sub_line_items: []
          };
        }

        // Only add to sub_line_items if it's not a single item with empty li_subtext
        if (!(acc[item.li_text].sub_line_items.length === 0 && item.li_subtext === '')) {
          acc[item.li_text].sub_line_items.push(item);
        }

        const main = acc[item.li_text].main_line_item;
        main.li_amount += Number(item.li_amount);
        main.approved_amount += item.approved_amount;
        main.unapproved_amount += item.unapproved_amount;
        main.stats.approved_expenses.exceeding_budget += item.stats.approved_expenses.exceeding_budget;
        main.stats.approved_expenses.within_budget += item.stats.approved_expenses.within_budget;
        main.stats.unapproved_expenses.exceeding_budget += item.stats.unapproved_expenses.exceeding_budget;
        main.stats.unapproved_expenses.within_budget += item.stats.unapproved_expenses.within_budget;
        main.stats.pending_expenses.pending_amount += Number(item.stats.pending_expenses?.pending_amount ?? 0);
        main.stats.pending_expenses.pending_within_budget += Number(
          item.stats.pending_expenses?.pending_within_budget ?? 0
        );
        main.stats.pending_expenses.pending_exceeding_budget += Number(
          item.stats.pending_expenses?.pending_exceeding_budget ?? 0
        );
        main.stats.draft_expenses.draft_amount += Number(item.stats.draft_expenses?.draft_amount ?? 0);
        main.stats.draft_expenses.draft_within_budget += Number(item.stats.draft_expenses?.draft_within_budget ?? 0);
        main.stats.draft_expenses.draft_exceeding_budget += Number(
          item.stats.draft_expenses?.draft_exceeding_budget ?? 0
        );
        main.stats.total_budget_amount =
          Number(main.stats.total_budget_amount) + Number(item.stats.total_budget_amount);

        // For simple items (no sub-text) carry note + calendar onto the aggregate header
        if (item.li_subtext === '') {
          main.li_note = item.li_note || '';
          main.li_calendar = item.li_calendar;
        }

        return acc;
      },
      {} as Record<string, any>
    );
  });

  const groupedArray: Ref<StructuredItems[]> = computed(() => Object.values(structuredItems.value));

  const expandablePanelIndices = computed(() =>
    groupedArray.value
      .map((group, index) => (group.sub_line_items.length > 0 ? index : -1))
      .filter(index => index !== -1)
  );

  const activeKey: Ref<number[]> = ref(computed(() => expandablePanelIndices.value).value);
  const isExpanded = ref(true);

  const handleToggleAll = () => {
    if (isExpanded.value) {
      activeKey.value = [];
    } else {
      activeKey.value = expandablePanelIndices.value;
    }
    isExpanded.value = !isExpanded.value;
  };

  onMounted(() => {
    activeKey.value = expandablePanelIndices.value;
  });
</script>

<template>
  <ComponentSection title="Budget Line Item">
    <template #callToAction>
      <a-button
        class="bg-transparent border-none shadow-none sm-medium text-zinc-600 p-0 h-fit w-fit"
        @click="handleToggleAll"
      >
        {{ isExpanded ? 'Collapse All' : 'Expand All' }}
      </a-button>
    </template>
    <a-collapse
      v-for="(group, index) in groupedArray"
      :key="index"
      v-model:active-key="activeKey"
      collapsible="header"
      class="budget-line-item"
    >
      <template #expandIcon="{ isActive }">
        <ChevronUpIcon class="text-black w-4 h-4" :class="{ 'rotate-180': !isActive }" />
      </template>
      <a-collapse-panel
        :key="index"
        :collapsible="group.sub_line_items.length <= 0 ? 'disabled' : 'header'"
        :show-arrow="group.sub_line_items.length <= 0 ? false : true"
      >
        <template #header>
          <div class="w-full h-full flex items-center gap-3 px-3 py-2">
            <!-- Name: takes all available space, wraps when too long -->
            <div class="flex-1 min-w-0">
              <p class="sm-medium text-zinc-800">{{ group?.main_line_item?.li_text }}</p>
            </div>
            <!-- Amount + icons + status: never shrinks or wraps -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <p class="sm-medium text-zinc-800 whitespace-nowrap">
                {{ formatToIndianCurrency(+group?.main_line_item?.li_amount) }}
              </p>

              <!-- Note icon: interactive for simple items, dimmed for parent-of-subs -->
              <div
                class="relative"
                :class="group.sub_line_items.length === 0 ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'"
                @click="
                  group.sub_line_items.length === 0 &&
                    (() => {
                      isModalVisible = true;
                      modalNote = group.main_line_item.li_note || '';
                    })()
                "
              >
                <AnnotationIcon />
                <div
                  v-if="group.sub_line_items.length === 0 && group.main_line_item.li_note"
                  class="absolute -top-0 -right-0 w-3 h-3 bg-blue-700 rounded-full"
                />
              </div>

              <!-- Breakdown icon: only for simple items (no sub-items) -->
              <div
                v-if="group.sub_line_items.length === 0"
                class="relative cursor-pointer"
                @click="openBreakdownView((group.main_line_item as any).li_calendar, group.main_line_item.li_amount)"
              >
                <PieGraphFilledIcon />
                <div
                  v-if="(group.main_line_item as any).li_calendar?.li_isCustom === 'true'"
                  class="absolute -top-[3px] -right-[3px] w-3 h-3 bg-blue-700 rounded-full"
                />
              </div>

              <BudgetExpensesStatus class="min-w-[380px]" :stats="group?.main_line_item?.stats" in-table-of="Budget" />
            </div>
            <NuxtLink
              target="_blank"
              :to="{
                path: '/expenses',
                query: {
                  budget: `${budgetId}-${budgetName}-${budgetFinancialYear}`,
                  budget_li: group.main_line_item.li_text,
                  lineItem: 'yes'
                }
              }"
              class="flex text-blue-700 hover:underline flex-shrink-0"
            >
              View
            </NuxtLink>
          </div>
        </template>
        <div
          v-for="item in group.sub_line_items"
          :key="item.li_text"
          class="flex flex-col overflow-hidden rounded-b-lg"
        >
          <div class="w-full h-full flex items-center gap-3 px-3 py-2 bg-zinc-50 border-t border-zinc-200">
            <!-- Sub-item name: takes available space, wraps when too long -->
            <div class="flex gap-2 items-center flex-1 min-w-0">
              <TopToRightAngleIcon class="flex-shrink-0 mb-2" />
              <p class="sm-medium text-zinc-800">{{ item?.li_subtext }}</p>
            </div>
            <!-- Amount + icons + status: never shrinks or wraps -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <p class="sm-medium text-zinc-800 whitespace-nowrap">{{ formatToIndianCurrency(+item?.li_amount) }}</p>

              <!-- Note icon for sub-item -->
              <div
                class="relative cursor-pointer"
                @click="
                  () => {
                    isModalVisible = true;
                    modalNote = item.li_note || '';
                  }
                "
              >
                <AnnotationIcon />
                <div v-if="item.li_note" class="absolute -top-0 -right-0 w-3 h-3 bg-blue-700 rounded-full" />
              </div>

              <!-- Breakdown icon for sub-item -->
              <div class="relative cursor-pointer" @click="openBreakdownView(item.li_calendar, item.li_amount)">
                <PieGraphFilledIcon />
                <div
                  v-if="item.li_calendar?.li_isCustom === 'true'"
                  class="absolute -top-[3px] -right-[3px] w-3 h-3 bg-blue-700 rounded-full"
                />
              </div>

              <BudgetExpensesStatus class="min-w-[380px]" :stats="item?.stats" in-table-of="Budget" />
            </div>
            <NuxtLink
              target="_blank"
              :to="{
                path: '/expenses',
                query: {
                  budget: `${budgetId}-${budgetName}-${budgetFinancialYear}`,
                  budget_li: group.main_line_item.li_text + ' - ' + item?.li_subtext,
                  lineItem: 'yes'
                }
              }"
              class="flex text-blue-700 hover:underline flex-shrink-0"
            >
              View
            </NuxtLink>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
    <!-- ── Read-only Note modal ──────────────────────────────────────────── -->
    <a-modal
      :open="isModalVisible"
      class="account-info-modal view-drawer-modal"
      title="Notes"
      @cancel="handleModalCancel"
    >
      <a-form layout="vertical">
        <a-form-item>
          <a-textarea :value="modalNote || ''" :rows="4" placeholder="No note added" disabled />
        </a-form-item>
      </a-form>
      <template #footer>
        <div class="flex gap-1 w-full justify-end border-t border-gray-300 p-3 bg-gray-50 rounded-b-lg">
          <a-button
            class="rounded text-sm font-medium border border-gray-300 text-gray-500 px-3 py-1.5"
            @click="handleModalCancel"
            >Close</a-button
          >
        </div>
      </template>
    </a-modal>

    <!-- ── Read-only Breakdown modal ─────────────────────────────────────── -->
    <a-modal
      :open="viewBreakdownVisible"
      class="breakdown-modal view-drawer-modal"
      @cancel="viewBreakdownVisible = false"
    >
      <div class="bg-gray-50 border-b border-gray-200 px-6 py-4 rounded-t-lg flex items-center gap-2">
        <PieGraphFilledIcon class="w-6 h-6" />
        <p class="text-sm font-medium">Breakdown Details</p>
      </div>
      <a-form layout="horizontal" :label-col="{ style: { width: '150px' } }" :colon="false" class="px-3 py-4">
        <div class="w-full flex items-center mb-4">
          <span class="text-gray-500 leading-[21px] font-medium">Breakdown Type</span>
          <div class="bg-gray-100 w-fit rounded border border-200 p-1 ml-auto pointer-events-none">
            <a-button
              :class="`mr-2 font-bold py-0.5 px-2 w-[75px] ${!viewBreakdownIsCustom ? 'shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F]' : 'bg-transparent border border-gray-100 font-medium shadow-none'} !leading-[21px]`"
              >Monthly</a-button
            >
            <a-button
              :class="`font-bold py-0.5 px-2 w-[72px] ${viewBreakdownIsCustom ? 'shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F]' : 'bg-inherit border border-gray-100 font-medium shadow-none'}`"
              >Custom</a-button
            >
          </div>
        </div>
        <div class="border border-gray-200 rounded-lg">
          <a-form-item
            label="Total Amount"
            class="text-black font-medium leading-[21px] breakdown-modal-label rounded-t-lg"
          >
            <a-input :value="viewBreakdownTotal" addon-before="₹" type="number" disabled />
          </a-form-item>
          <a-form-item
            v-for="entry in viewBreakdownEntries"
            :key="entry.month"
            :label="entry.month"
            class="text-black font-medium leading-[21px] border-t border-gray-200 breakdown-modal-label"
          >
            <a-input :value="entry.amount" addon-before="₹" type="number" disabled />
          </a-form-item>
        </div>
        <p v-if="viewBreakdownEntries.length === 0" class="text-sm text-gray-400 text-center py-4">
          No breakdown data available
        </p>
      </a-form>
      <template #footer>
        <div class="flex gap-1 w-full justify-end border-t border-gray-300 p-3 bg-gray-50 rounded-b-lg">
          <a-button
            class="rounded text-sm font-medium border border-gray-300 text-gray-500 px-3 py-1.5"
            @click="viewBreakdownVisible = false"
            >Close</a-button
          >
        </div>
      </template>
    </a-modal>
  </ComponentSection>
</template>

<style>
  @import '~/assets/css/modal.css';

  .budget-line-item .ant-collapse-header {
    padding: 0px !important;
    display: flex;
    position: relative;
  }

  .budget-line-item .ant-collapse-header-text {
    width: 100%;
  }

  .budget-line-item .ant-collapse-expand-icon {
    position: absolute;
    left: 10px;
    border: 1px solid #d4d4d8;
    padding: 0px !important;
    bottom: -10px;
    background: #fff;
    height: 20px !important;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    z-index: 1;
  }

  .budget-line-item .ant-collapse-expand-icon svg {
    transition: transform 0.2s ease;
  }

  .budget-line-item .ant-collapse-content-box {
    padding: 0px !important;
  }

  /* Breakdown modal — matches LineItemTable.vue */
  .breakdown-modal .ant-modal-content {
    padding: 0px !important;
  }

  .breakdown-modal .ant-form-item {
    margin-bottom: 0px !important;
  }

  .breakdown-modal .breakdown-modal-label .ant-form-item-label {
    border-right: 1px solid #e5e7eb;
    background-color: #f9fafb;
    margin-right: 8px;
  }

  .breakdown-modal .breakdown-modal-label .ant-form-item-label label {
    margin-top: 8px !important;
    margin-bottom: 8px !important;
  }

  .breakdown-modal .ant-form-item-control .ant-form-item-control-input-content {
    margin-top: 8px !important;
    margin-right: 8px !important;
  }

  /* ── Drawer view-only: disabled field readability ─────────────────────── */
  .view-drawer-modal textarea.ant-input[disabled],
  .view-drawer-modal .ant-input[disabled],
  .view-drawer-modal input[disabled] {
    color: #1f2937 !important;
    -webkit-text-fill-color: #1f2937 !important;
    background-color: #f9fafb !important;
    cursor: default !important;
    opacity: 1 !important;
  }

  .view-drawer-modal textarea.ant-input[disabled]::placeholder {
    color: #9ca3af !important;
    -webkit-text-fill-color: #9ca3af !important;
  }

  .view-drawer-modal .ant-input-group-wrapper-disabled .ant-input-group-addon {
    color: #374151 !important;
    background-color: #f3f4f6 !important;
    opacity: 1 !important;
  }

  .view-drawer-modal .ant-input-group-wrapper-disabled {
    opacity: 1 !important;
  }
</style>
